import { useState, useEffect, useMemo } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  BadgeDollarSign,
  CheckCircle2,
  Hash,
  Mail,
  MapPin,
  Package,
  ShieldCheck,
  User,
  XCircle,
  Zap,
} from 'lucide-react';
import {
  DescriptionText,
  Field,
  HorizontalStack,
  PropertyGrid,
  PropertyItem,
  RegistryStatus,
  SectionSeparator,
  Surface,
  VerticalStack,
  cn,
} from '@workspace/silk-ui';
import {
  type Order,
  type OrderAction,
  type TimelineEvent,
  type TimelineNote,
  type OrderWorkspace,
} from '@/lib/commerce';
import {
  buildOrderPricingSummary,
  formatOrderCurrency,
  normalizeOrderLineItems,
} from './orderLineItems';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(value?: string | null): string {
  if (!value) return 'Not recorded';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString();
}

function formatRelativeTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const diffMs = Date.now() - d.getTime();
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function formatJSON(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function orderReference(order: Pick<Order, 'friendly_order_id' | 'order_id'>): string {
  return order.friendly_order_id || order.order_id;
}

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
    case 'completed':
      return 'success';
    case 'submitted':
    case 'processing':
      return 'warning';
    case 'cancelled':
    case 'failed':
      return 'error';
    default:
      return 'muted';
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function formatBoolean(value: unknown): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
}

function formatCommissionAmount(snapshot: Record<string, unknown> | null): string {
  if (!snapshot) return '—';
  const amount = snapshot.amount;
  if (typeof amount === 'number') return amount.toString();
  if (typeof amount === 'string' && amount.trim()) {
    const currency = typeof snapshot.currency === 'string' && snapshot.currency.trim() ? snapshot.currency : '';
    return currency ? `${amount} ${currency}` : amount;
  }
  return '—';
}

function isDestructiveAction(action: OrderAction): boolean {
  const text = `${action.label || ''} ${action.category || ''}`.toLowerCase();
  return ['cancel', 'reject', 'refund', 'void', 'delete', 'remove', 'revert'].some(w => text.includes(w));
}

function getActionIcon(action: OrderAction) {
  if (isDestructiveAction(action)) return XCircle;
  const lbl = (action.label || '').toLowerCase();
  if (lbl.includes('send') || lbl.includes('email') || lbl.includes('notify')) return Mail;
  if (lbl.includes('complete') || lbl.includes('activate') || lbl.includes('approve')) return CheckCircle2;
  return Zap;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const primaryButtonClass =
  'inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-black text-primary-foreground shadow-sm transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const secondaryButtonClass =
  'inline-flex items-center justify-center gap-1.5 rounded-md border border-border/60 bg-background px-3 py-1.5 text-xs font-bold text-foreground transition-all hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-50';
const destructiveButtonClass =
  'inline-flex items-center justify-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-1.5 text-xs font-bold text-destructive transition-all hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50';
const textareaStyles =
  'w-full resize-none rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm text-foreground outline-none transition-all duration-150 focus:border-primary/40 focus:bg-background placeholder:text-muted-foreground/60 disabled:opacity-50';

const INITIAL_VISIBLE_NOTES = 8;
const NOTE_LOAD_INCREMENT = 8;

const ORDER_EVENT_LABELS: Record<string, string> = {
  order_note_added: 'Note added',
};

function normalizeEventTypeKey(eventType?: string | null): string {
  return `${eventType ?? ''}`.trim().toLowerCase().replace(/[.\s]+/g, '_');
}

function formatEventTitle(eventType?: string | null): string {
  const key = normalizeEventTypeKey(eventType);
  if (!key) return 'Activity event';
  return ORDER_EVENT_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function resolveEventNoteId(event: TimelineEvent): string | null {
  const payload = asRecord(event.payload);
  const noteID = payload?.note_id;
  return typeof noteID === 'string' && noteID.trim() ? noteID.trim() : null;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function DataPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground/50">{label}</span>
      <span className="text-xs font-bold text-foreground">{value || '—'}</span>
    </div>
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'actions' | 'notes' | 'activity';

// ─── Props ───────────────────────────────────────────────────────────────────

interface OrderViewProps {
  order: Order | null;
  workspace: OrderWorkspace;
  notes: TimelineNote[];
  events: TimelineEvent[];
  actions: OrderAction[];
  loading?: boolean;
  onClose: () => void;
  onStatusUpdate: (status: string) => Promise<void>;
  onOrderAction: (actionId: string) => Promise<void>;
  onConfirmation: (mode: 'resend' | 'regenerate') => Promise<void>;
  onAddNote: (note: string) => Promise<void>;
  onApproveCommission: (comment?: string) => Promise<void>;
  onRejectCommission: (comment?: string) => Promise<void>;
  submittingKey: string | null;
  message: string | null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function OrderView({
  order,
  workspace,
  notes,
  events,
  actions,
  loading,
  onClose,
  onOrderAction,
  onConfirmation,
  onAddNote,
  onApproveCommission,
  onRejectCommission,
  submittingKey,
  message,
}: OrderViewProps) {
  const isResellerWorkspace = workspace === 'reseller';

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [noteDraft, setNoteDraft] = useState('');
  const [noteFocused, setNoteFocused] = useState(false);
  const [visibleNoteCount, setVisibleNoteCount] = useState(INITIAL_VISIBLE_NOTES);
  const [commissionCommentDraft, setCommissionCommentDraft] = useState('');
  const [expandedEventIds, setExpandedEventIds] = useState<Set<string>>(new Set());
  const [toastVisible, setToastVisible] = useState(false);

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    setActiveTab('overview');
    setExpandedEventIds(new Set());
    setNoteDraft('');
    setNoteFocused(false);
    setVisibleNoteCount(INITIAL_VISIBLE_NOTES);
  }, [order?.order_id]);

  useEffect(() => {
    if (!message) return;
    setToastVisible(true);
    const t = setTimeout(() => setToastVisible(false), 4000);
    return () => clearTimeout(t);
  }, [message]);

  // ── Derived data ───────────────────────────────────────────────────────────
  const sortedNotes = useMemo(
    () =>
      [...notes].sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()),
    [notes],
  );

  const sortedEvents = useMemo(
    () =>
      [...events].sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()),
    [events],
  );

  const visibleNotes = useMemo(
    () => sortedNotes.slice(0, visibleNoteCount),
    [sortedNotes, visibleNoteCount],
  );

  const hasMoreNotes = visibleNoteCount < sortedNotes.length;

  const notesById = useMemo(
    () => new Map(sortedNotes.map(note => [note.note_id, note])),
    [sortedNotes],
  );

  const lineItems = useMemo(
    () => normalizeOrderLineItems(order?.line_items_snapshot),
    [order?.line_items_snapshot],
  );

  const pricingSummary = useMemo(
    () => buildOrderPricingSummary(order?.pricing_snapshot, lineItems),
    [lineItems, order?.pricing_snapshot],
  );

  // ── Handlers ───────────────────────────────────────────────────────────────
  const submitNote = async () => {
    const body = noteDraft.trim();
    if (!body || submittingKey !== null) return;
    await onAddNote(body);
    setNoteDraft('');
    setNoteFocused(false);
  };

  const handleNoteKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void submitNote();
    }
  };

  const toggleEventExpanded = (eventId: string) => {
    setExpandedEventIds(prev => {
      const next = new Set(prev);
      next.has(eventId) ? next.delete(eventId) : next.add(eventId);
      return next;
    });
  };

  // ── Early returns ──────────────────────────────────────────────────────────
  if (loading && !order) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <VerticalStack gap={4} className="items-center text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <DescriptionText className="text-sm font-bold animate-pulse">Loading order details...</DescriptionText>
        </VerticalStack>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <VerticalStack gap={4} className="items-center text-center max-w-sm">
          <div className="rounded-full bg-muted p-4">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <p className="text-lg font-black tracking-tight">Order Not Found</p>
            <DescriptionText className="text-sm">The order details could not be loaded or no longer exists.</DescriptionText>
          </div>
          <button onClick={onClose} className="mt-2 rounded-md bg-primary px-6 py-2 text-sm font-black text-primary-foreground transition-all hover:opacity-90">
            Back to Queue
          </button>
        </VerticalStack>
      </div>
    );
  }

  // ── Computed ───────────────────────────────────────────────────────────────
  const pricingSnapshot = asRecord(order.pricing_snapshot);
  const commissionSnapshot = asRecord(order.commission_snapshot);
  const eligibilitySnapshot = asRecord(order.eligibility_snapshot);
  const discountsSnapshot = asRecord(order.discounts_snapshot);
  const discountCodeCount = Array.isArray(discountsSnapshot?.codes) ? discountsSnapshot.codes.length : 0;
  const hasPendingCommissionProposal =
    !isResellerWorkspace &&
    commissionSnapshot?.mode === 'automatic_deferred' &&
    commissionSnapshot?.proposal_status === 'pending_approval';

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    ...(!isResellerWorkspace ? [{ id: 'actions' as TabId, label: 'Actions', count: actions.length }] : []),
    ...(!isResellerWorkspace ? [{ id: 'notes' as TabId, label: 'Notes', count: sortedNotes.length || undefined }] : []),
    { id: 'activity', label: 'Activity', count: sortedEvents.length || undefined },
  ];

  // ── Sidebar ────────────────────────────────────────────────────────────────
  const sidebar = (
    <VerticalStack gap={6}>
      <VerticalStack gap={3}>
        <VerticalStack gap={2}>
          <div className="flex items-center gap-2 flex-wrap">
            <RegistryStatus label={order.status} variant={statusVariant(order.status)} />
            <span className="inline-flex rounded-md border border-border/60 bg-muted/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-muted-foreground">
              {order.lifecycle_phase}
            </span>
          </div>
          <p className="text-sm font-black text-foreground">{orderReference(order)}</p>
          <DescriptionText className="text-[10px]">{formatDate(order.updated_at)}</DescriptionText>
        </VerticalStack>
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(order.order_id)}
          className="flex items-center gap-1.5 text-left group w-full"
          title="Click to copy UUID"
        >
          <Hash className="h-3 w-3 text-muted-foreground/40 shrink-0" />
          <span className="font-mono text-[9px] text-muted-foreground/40 truncate group-hover:text-muted-foreground transition-colors">
            {order.order_id}
          </span>
        </button>
      </VerticalStack>

      {!isResellerWorkspace && (
        <VerticalStack gap={3}>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 px-1">Confirmation</p>
          <div className="grid grid-cols-1 gap-2">
            <button type="button" className={secondaryButtonClass} onClick={() => void onConfirmation('resend')} disabled={submittingKey !== null}>
              {submittingKey === 'resend' ? 'Sending...' : 'Resend Confirmation'}
            </button>
            <button type="button" className={secondaryButtonClass} onClick={() => void onConfirmation('regenerate')} disabled={submittingKey !== null}>
              {submittingKey === 'regenerate' ? 'Regenerating...' : 'Regenerate Confirmation'}
            </button>
          </div>
        </VerticalStack>
      )}
    </VerticalStack>
  );

  // ── Mobile top band ────────────────────────────────────────────────────────
  const mobileTopBand = (
    <div className="lg:hidden shrink-0 border-b border-border/40 bg-muted/5 px-4 py-3 sm:px-6">
      <div className="flex items-center gap-2 flex-wrap">
        <RegistryStatus label={order.status} variant={statusVariant(order.status)} />
        <span className="inline-flex rounded-md border border-border/60 bg-muted/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-muted-foreground">
          {order.lifecycle_phase}
        </span>
        <span className="ml-auto font-mono text-[10px] font-bold text-foreground/60 truncate max-w-[160px]" title={orderReference(order)}>
          {orderReference(order)}
        </span>
      </div>
      {!isResellerWorkspace && (
        <div className="flex gap-2 mt-3">
          <button type="button" className={cn(secondaryButtonClass, 'flex-1 text-[11px]')} onClick={() => void onConfirmation('resend')} disabled={submittingKey !== null}>
            {submittingKey === 'resend' ? 'Sending…' : 'Resend Confirmation'}
          </button>
          <button type="button" className={cn(secondaryButtonClass, 'flex-1 text-[11px]')} onClick={() => void onConfirmation('regenerate')} disabled={submittingKey !== null}>
            {submittingKey === 'regenerate' ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      )}
    </div>
  );

  // ── Tab content ────────────────────────────────────────────────────────────

  const overviewContent = (
    <VerticalStack gap={8} className="pt-6">
      {/* Customer + Address — compact 2-col */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Surface className="p-4 border-border/40 hover:border-primary/20 transition-colors">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <VerticalStack gap={0.5} className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">Customer</p>
              <p className="text-sm font-bold text-foreground leading-snug">
                {`${order.customer_snapshot?.first_name || ''} ${order.customer_snapshot?.last_name || ''}`.trim() || '—'}
              </p>
              <p className="text-xs text-muted-foreground truncate">{order.customer_snapshot?.email || 'No email'}</p>
              {order.customer_snapshot?.phone && (
                <p className="text-xs text-muted-foreground">{order.customer_snapshot.phone}</p>
              )}
            </VerticalStack>
          </div>
        </Surface>

        <Surface className="p-4 border-border/40 hover:border-primary/20 transition-colors">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <VerticalStack gap={0.5} className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">Service Address</p>
              <p className="text-sm font-bold text-foreground leading-snug">
                {order.service_address_snapshot?.formatted_address || '—'}
              </p>
            </VerticalStack>
          </div>
        </Surface>
      </div>

      <SectionSeparator />

      {/* Commercial State — flat data row */}
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground/60 mb-3">Commercial State</p>
        <div className="rounded-lg border border-border/40 bg-muted/5 px-4 py-3">
          <div className="flex flex-wrap gap-x-5 gap-y-2 divide-x divide-border/30">
            <DataPill label="Pricing" value={(pricingSnapshot?.status as string) || '—'} />
            <div className="pl-5"><DataPill label="Discounts" value={discountCodeCount > 0 ? `${discountCodeCount} applied` : 'None'} /></div>
            <div className="pl-5"><DataPill label="Eligibility" value={(eligibilitySnapshot?.status as string) || '—'} /></div>
            <div className="pl-5">
              {isResellerWorkspace
                ? <DataPill label="Commission" value={formatCommissionAmount(commissionSnapshot)} />
                : <DataPill label="Commission" value={(commissionSnapshot?.mode as string) || '—'} />
              }
            </div>
          </div>
        </div>
      </div>

      <SectionSeparator />

      {/* Line Items */}
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground/60 mb-3">Line Items</p>
        {lineItems.length > 0 ? (
          <div className="rounded-lg border border-border/40 overflow-hidden">
            <div className="divide-y divide-border/30">
              {lineItems.map(item => (
                <div key={item.key} className="flex flex-col gap-4 px-4 py-4 transition-colors hover:bg-muted/5 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/60">
                      <Package className="h-4 w-4 text-muted-foreground/60" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-black text-foreground" title={item.title}>{item.title}</p>
                      {(item.productName || item.offerName) ? (
                        <p className="mt-0.5 text-xs font-medium text-muted-foreground">
                          {[item.productName, item.offerName].filter(Boolean).join(' · ')}
                        </p>
                      ) : null}
                      {item.description ? (
                        <p className="mt-1.5 max-w-2xl text-xs leading-relaxed text-muted-foreground/80">{item.description}</p>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="rounded-md border border-border/50 bg-muted/20 px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
                          Qty {item.quantity}
                        </span>
                        {[item.billingLabel, item.serviceClass, item.planLabel, item.termLabel].filter(Boolean).map(label => (
                          <span key={label} className="rounded-md border border-border/50 bg-muted/20 px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
                            {label}
                          </span>
                        ))}
                        {item.includedAtNoCost ? (
                          <span className="rounded-md border border-border/50 bg-muted/20 px-2 py-0.5 text-[10px] font-bold text-foreground">
                            Included at no cost
                          </span>
                        ) : null}
                        {item.discountCount > 0 ? (
                          <span className="rounded-md border border-success/30 bg-success/5 px-2 py-0.5 text-[10px] font-bold text-success">
                            {item.discountCount} {item.discountCount === 1 ? 'discount' : 'discounts'}
                          </span>
                        ) : null}
                        {item.feeCount > 0 ? (
                          <span className="rounded-md border border-border/50 bg-muted/20 px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
                            {item.feeCount} {item.feeCount === 1 ? 'fee' : 'fees'}
                          </span>
                        ) : null}
                      </div>
                      {item.identifiers.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                          {item.identifiers.map(identifier => (
                            <span key={`${identifier.label}-${identifier.value}`} className="text-[10px] text-muted-foreground/60">
                              <span className="font-bold">{identifier.label}</span>{' '}
                              <span className="font-mono">{identifier.value}</span>
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="shrink-0 pl-11 text-left sm:min-w-36 sm:pl-4 sm:text-right">
                    {item.originalUnitPriceMinor != null ? (
                      <p className="text-[10px] text-muted-foreground line-through">
                        {formatOrderCurrency(item.originalUnitPriceMinor, item.currency)}
                      </p>
                    ) : null}
                    <p className="font-mono text-sm font-black text-foreground">
                      {formatOrderCurrency(item.unitPriceMinor, item.currency)}
                    </p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">
                      {item.billingLabel ? `${item.billingLabel} · ` : ''}per item
                    </p>
                    {item.quantity > 1 && item.lineTotalMinor != null ? (
                      <p className="mt-1 text-xs font-bold text-foreground">
                        {formatOrderCurrency(item.lineTotalMinor, item.currency)} total
                      </p>
                    ) : null}
                    {item.startupFeeMinor != null && item.startupFeeMinor !== 0 ? (
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {formatOrderCurrency(item.startupFeeMinor, item.currency)} startup
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {pricingSummary.length > 0 ? (
              <div className="grid gap-px border-t border-border/40 bg-border/30 sm:grid-cols-2 lg:grid-cols-4">
                {pricingSummary.map(summary => (
                  <div key={summary.label} className="flex items-center justify-between bg-muted/10 px-4 py-3 sm:block sm:text-right">
                    <span className="text-[10px] font-black uppercase tracking-wide text-muted-foreground/60">{summary.label}</span>
                    <p className="font-mono text-sm font-black text-foreground">
                      {formatOrderCurrency(summary.amountMinor, summary.currency)}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic px-1">No line items in this order.</p>
        )}
      </div>
    </VerticalStack>
  );

  const actionsContent = (
    <div className="pt-6">
      {actions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
            <Zap className="h-4 w-4 text-muted-foreground/50" />
          </div>
          <p className="text-sm font-bold text-foreground">No actions available</p>
          <p className="mt-1 text-xs text-muted-foreground">This order has no permitted operations in its current state.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border/40 overflow-hidden divide-y divide-border/30">
          {actions.map(action => {
            const blockedReasons = Array.isArray(action.blocked_reasons) ? action.blocked_reasons : [];
            const isBlocked = blockedReasons.length > 0;
            const isRunning = submittingKey === action.action_id;
            const destructive = isDestructiveAction(action);
            const ActionIcon = getActionIcon(action);

            return (
              <div
                key={action.action_id}
                className={cn(
                  'flex items-center gap-4 px-4 py-3 transition-colors',
                  isBlocked ? 'opacity-50' : 'hover:bg-muted/5'
                )}
              >
                <div className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
                  destructive ? 'bg-destructive/10' : 'bg-muted/60'
                )}>
                  <ActionIcon className={cn('h-3.5 w-3.5', destructive ? 'text-destructive' : 'text-muted-foreground')} />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground leading-snug">{action.label}</p>
                  <p className="text-[10px] text-muted-foreground/70 leading-snug">
                    {action.category}{action.state ? ` · ${action.state}` : ''}
                    {isBlocked && ` · ${blockedReasons.join(', ')}`}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => void onOrderAction(action.action_id)}
                  disabled={submittingKey !== null || isBlocked}
                  title={isBlocked ? blockedReasons.join('; ') : undefined}
                  className={cn(
                    'shrink-0',
                    destructive ? destructiveButtonClass : primaryButtonClass,
                    isBlocked && 'pointer-events-none'
                  )}
                >
                  {isRunning ? (
                    <>
                      <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Running
                    </>
                  ) : isBlocked ? (
                    '—'
                  ) : (
                    'Run'
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const notesContent = (
    <div className="pt-6">
      <div className="mb-8">
        <textarea
          value={noteDraft}
          onChange={e => setNoteDraft(e.target.value)}
          onFocus={() => setNoteFocused(true)}
          onBlur={() => { if (!noteDraft.trim()) setNoteFocused(false); }}
          onKeyDown={handleNoteKeyDown}
          placeholder="Add a note..."
          rows={noteFocused ? 4 : 1}
          className={cn(textareaStyles, 'min-h-[38px]', noteFocused && 'min-h-[100px]')}
        />
        {noteFocused && (
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground/60">⌘ Return to submit</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setNoteDraft(''); setNoteFocused(false); }}
                className={secondaryButtonClass}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submitNote()}
                disabled={!noteDraft.trim() || submittingKey !== null}
                className={primaryButtonClass}
              >
                {submittingKey === 'note' ? 'Saving...' : 'Add Note'}
              </button>
            </div>
          </div>
        )}
      </div>

      {sortedNotes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-sm font-bold text-foreground">No notes yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Order notes will appear here as collaborators add context.</p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between gap-3">
            <DescriptionText className="text-[11px] font-bold text-muted-foreground/70">
              Showing {visibleNotes.length} of {sortedNotes.length} notes
            </DescriptionText>
          </div>
          <div className="max-h-[min(60vh,36rem)] overflow-y-auto pr-2">
            <div className="space-y-0">
              {visibleNotes.map((note, index) => {
                const isLast = index === visibleNotes.length - 1;
                const wasUpdated =
                  Boolean(note.updated_at) &&
                  new Date(note.updated_at).getTime() !== new Date(note.created_at || '').getTime();
                const initial = (note.author_user_id || 'S').charAt(0).toUpperCase();
                return (
                  <div key={note.note_id} className="relative flex gap-4 pb-6">
                    {!isLast && <div className="absolute left-[13px] top-7 bottom-0 w-px bg-border/40" />}
                    <div className="relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                      <span className="text-[9px] font-black text-primary">{initial}</span>
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="mb-1 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <span className="text-xs font-bold text-foreground">{note.author_user_id || 'System'}</span>
                          <p className="text-[10px] text-muted-foreground/60" title={formatDate(note.created_at)}>
                            Created {formatDate(note.created_at)}
                          </p>
                          {wasUpdated ? (
                            <p className="text-[10px] text-muted-foreground/50" title={formatDate(note.updated_at)}>
                              Updated {formatDate(note.updated_at)}
                            </p>
                          ) : null}
                        </div>
                        <span
                          className="shrink-0 tabular-nums text-[10px] text-muted-foreground"
                          title={formatDate(note.created_at)}
                        >
                          {formatRelativeTime(note.created_at)}
                        </span>
                      </div>
                      <div className="rounded-lg border border-border/40 bg-muted/5 px-3.5 py-2.5">
                        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">{note.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {hasMoreNotes ? (
            <div className="pt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleNoteCount(current => Math.min(current + NOTE_LOAD_INCREMENT, sortedNotes.length))}
                className={secondaryButtonClass}
              >
                Load more notes
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  const activityContent = (
    <div className="pt-6">
      {sortedEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-sm font-bold text-foreground">No activity yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Workflow events and system history will appear here as the order progresses.</p>
        </div>
      ) : (
        <div className="space-y-0">
          {sortedEvents.map((event, index) => {
            const isLast = index === sortedEvents.length - 1;
            const isExpanded = expandedEventIds.has(event.event_id);
            const linkedNote = notesById.get(resolveEventNoteId(event) ?? '');
            const hasPayload = asRecord(event.payload) && Object.keys(asRecord(event.payload) ?? {}).length > 0;
            return (
              <div key={event.event_id} className="relative flex gap-4 pb-6">
                {!isLast && <div className="absolute left-[13px] top-7 bottom-0 w-px bg-border/40" />}
                <div className="relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted border border-border/60">
                  <Zap className="h-3 w-3 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-xs font-bold text-foreground">{formatEventTitle(event.event_type)}</span>
                    <span
                      className="text-[10px] text-muted-foreground shrink-0 tabular-nums"
                      title={formatDate(event.created_at)}
                    >
                      {formatRelativeTime(event.created_at)}
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                    {event.actor_user_id || 'System'}
                  </p>
                  {linkedNote ? (
                    <div className="mt-2 rounded-lg border border-border/40 bg-muted/10 overflow-hidden">
                      <div className="flex items-start justify-between gap-3 border-b border-border/25 bg-background/80 px-3.5 py-2">
                        <div className="min-w-0">
                          <p className="text-[9px] font-black uppercase tracking-[0.18em] text-muted-foreground/55">
                            Linked Note
                          </p>
                          <p className="text-[11px] font-bold text-foreground">
                            {linkedNote.author_user_id || 'System'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] text-muted-foreground">{formatRelativeTime(linkedNote.created_at)}</p>
                          <p className="text-[9px] text-muted-foreground/55" title={formatDate(linkedNote.created_at)}>
                            {formatDate(linkedNote.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="px-3.5 py-3">
                        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
                          {linkedNote.body}
                        </p>
                      </div>
                      <div className="px-3.5 pb-3">
                        <button
                          type="button"
                          onClick={() => setActiveTab('notes')}
                          className="text-[10px] font-bold text-primary hover:underline"
                        >
                          Open in Notes
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {hasPayload ? (
                    <>
                      {isExpanded && (
                        <div className="mt-2 rounded-md border border-border/30 bg-muted/20 overflow-hidden">
                          <div className="flex items-center justify-between border-b border-border/20 bg-background px-3 py-1.5">
                            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/50">payload.json</span>
                            <button
                              type="button"
                              onClick={() => void navigator.clipboard.writeText(formatJSON(event.payload))}
                              className="text-[9px] font-black text-primary hover:underline"
                            >
                              Copy
                            </button>
                          </div>
                          <pre className="max-h-[200px] overflow-x-auto p-3 text-[10px] font-mono leading-relaxed text-foreground/70">
                            {formatJSON(event.payload)}
                          </pre>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleEventExpanded(event.event_id)}
                        className="mt-1.5 text-[9px] font-bold text-primary/60 hover:text-primary transition-colors"
                      >
                        {isExpanded ? 'hide payload' : 'show payload'}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col bg-background">
      {/* Auto-dismiss toast */}
      {toastVisible && message && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-lg border border-border/60 bg-background px-4 py-3 shadow-lg shadow-black/10 animate-in slide-in-from-bottom-2 duration-200">
          <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
          <p className="text-sm font-bold text-foreground">{message}</p>
        </div>
      )}

      {/* Header */}
      <div className="shrink-0 border-b border-border/40 px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-lg font-black tracking-tight text-foreground">Order {orderReference(order)}</p>
            <p className="text-sm text-muted-foreground">
              {`${order.customer_snapshot?.first_name || ''} ${order.customer_snapshot?.last_name || ''}`.trim() || 'Unassigned Customer'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-all hover:bg-muted"
            aria-label="Close panel"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Body: sidebar + main */}
      <div className="flex flex-1 min-h-0">
        {/* Desktop sidebar */}
        <div className="hidden lg:flex w-56 xl:w-64 shrink-0 flex-col border-r border-border/40 overflow-y-auto p-6">
          {sidebar}
        </div>

        {/* Main column */}
        <div className="flex flex-1 flex-col min-w-0 min-h-0">
          {mobileTopBand}

          {/* Scrollable area */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="pb-6">
              {/* Commission proposal banner — always above tabs when pending */}
              {hasPendingCommissionProposal && (
                <Surface className="rounded-none border-x-0 border-t-0 border-amber-400/40 bg-amber-500/5 px-6 py-5">
                  <VerticalStack gap={4}>
                    <HorizontalStack gap={3} className="items-start">
                      <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                      <VerticalStack gap={1} className="flex-1">
                        <p className="text-sm font-black text-foreground">Commission Proposal Pending Approval</p>
                        <DescriptionText className="text-xs">
                          A deferred commission proposal requires your review before the ledger entry is finalized.
                        </DescriptionText>
                      </VerticalStack>
                    </HorizontalStack>
                    <PropertyGrid cols={4}>
                      <PropertyItem label="Mode" value={(commissionSnapshot?.mode as string) || '—'} />
                      <PropertyItem label="Status" value={(commissionSnapshot?.proposal_status as string) || '—'} />
                      <PropertyItem label="Amount" value={(commissionSnapshot?.proposal_amount as string) || '—'} />
                      <PropertyItem label="Currency" value={(commissionSnapshot?.currency as string) || '—'} />
                    </PropertyGrid>
                    <Field label="Review comment (optional)">
                      <textarea
                        rows={2}
                        value={commissionCommentDraft}
                        onChange={e => setCommissionCommentDraft(e.target.value)}
                        placeholder="Add a note for this decision..."
                        className={textareaStyles}
                      />
                    </Field>
                    <HorizontalStack className="justify-end gap-2">
                      <button type="button" className={destructiveButtonClass} onClick={() => void onRejectCommission(commissionCommentDraft.trim())} disabled={submittingKey !== null}>
                        {submittingKey === 'reject-commission' ? 'Rejecting...' : 'Reject'}
                      </button>
                      <button type="button" className={primaryButtonClass} onClick={() => void onApproveCommission(commissionCommentDraft.trim())} disabled={submittingKey !== null}>
                        {submittingKey === 'approve-commission' ? 'Approving...' : 'Approve'}
                      </button>
                    </HorizontalStack>
                  </VerticalStack>
                </Surface>
              )}

              {/* Sticky tab bar */}
              <div className="sticky top-0 z-10 flex border-b border-border/40 bg-background">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex items-center gap-1.5 px-4 py-3 text-xs font-bold transition-all border-b-2 -mb-px',
                      activeTab === tab.id
                        ? 'border-primary text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border/60'
                    )}
                  >
                    {tab.label}
                    {tab.count !== undefined && tab.count > 0 && (
                      <span className={cn(
                        'rounded-full px-1.5 py-px text-[9px] font-black tabular-nums',
                        activeTab === tab.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      )}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="px-1">
                {activeTab === 'overview' && overviewContent}
                {activeTab === 'actions' && actionsContent}
                {activeTab === 'notes' && notesContent}
                {activeTab === 'activity' && activityContent}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
